/**
 * The Help menu (docs/specs/support.md): Help center, What's new, Keyboard shortcuts, Ask Support, Send feedback…
 * and the app version. Pinned to the bottom of the activity bar (opens to the right, bottom-aligned); below 820px,
 * where there is no bar, the same rows sit in the sidebar's profile menu. Also hosts the feedback sheet and its toast.
 */
import type { SupportDiagnostics, SupportRequestResult } from "@bundle/contracts";
import { FeedbackViewModel, Sessions, Shell, Support, type OpenTarget, type PickedFile } from "@bundle/core";
import { FeedbackSheet, IconButton, Menu, openLink, Toast, usePreferences, useT, useWorkspace, type MenuEntry } from "@bundle/ui";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { Container } from "../platform/container.ts";
import { isDesktop } from "../platform/desktop.ts";
import { APP_VERSION } from "../platform/env.ts";
import { systemClock } from "../platform/clock.ts";
import { markBetaFeedbackSent } from "../platform/beta.ts";
import { useUpdateMenuEntries } from "./AboutDialog.tsx";

const apple = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

type ToastState = { message: string; open?: string };

/** What the diagnostics read when they are sent: the screen I'm on, the connection and the language. */
class LiveContext {
  constructor(
    public activeUrl: string,
    public realtime: "connecting" | "online" | "offline",
    public locale: string,
  ) {}
  set(activeUrl: string, realtime: "connecting" | "online" | "offline", locale: string): void {
    this.activeUrl = activeUrl;
    this.realtime = realtime;
    this.locale = locale;
  }
}

/**
 * A picture of the window without the feedback sheet (modern-screenshot, loaded on first use). The sheet and its
 * scrim are left out; so are cross-origin frames the browser won't let us read.
 */
async function captureWindow(): Promise<PickedFile | undefined> {
  const { domToBlob } = await import("modern-screenshot");
  const blob = await domToBlob(document.body, {
    width: window.innerWidth,
    height: window.innerHeight,
    scale: Math.min(window.devicePixelRatio || 1, 2),
    type: "image/png",
    filter: (node) => !(node instanceof Element && (node.classList.contains("b-sheet") || node.classList.contains("b-sheet-overlay") || node.classList.contains("b-toast-viewport"))),
  });
  if (blob === null) return undefined;
  const name = `screenshot-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.png`;
  const file = new File([blob], name, { type: "image/png" });
  return { name, mime: file.type, size: file.size, lastModified: file.lastModified, source: file };
}

export type Help = {
  /** The menu rows (Help center … Send feedback…). */
  entries: MenuEntry[];
  /** An update is waiting: the Help button's dot. */
  badge: boolean;
  /** The feedback sheet and the toasts; render once. */
  host: ReactNode;
  /** Opens the feedback sheet (the beta banner's "Send feedback"). */
  openFeedback: () => void;
  /** The activity bar's Help menu is open (the Beta label opens it too). */
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
};

/** Everything the Help menu does, for the activity bar button and the phone profile menu. */
export function useHelp({ container, activeUrl, open, onShortcuts }: { container: Container; activeUrl: string; open: (url: string, target?: OpenTarget) => void; onShortcuts: () => void }): Help {
  const t = useT();
  const ws = useWorkspace();
  const prefs = usePreferences();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState<ToastState | undefined>(undefined);
  const platform = isDesktop() ? "desktop" : "web";

  // Read at send time: the screen I'm on (route type only) and the connection.
  const [live] = useState(() => new LiveContext(activeUrl, ws.state.realtime, prefs.locale));
  useEffect(() => live.set(activeUrl, ws.state.realtime, prefs.locale), [live, activeUrl, ws.state.realtime, prefs.locale]);
  const wsId = ws.vm.wsId;
  const vm = useMemo(
    () =>
      new FeedbackViewModel({
        ws: wsId,
        api: container.api,
        data: container.data,
        clock: systemClock,
        diagnostics: (): SupportDiagnostics => {
          const ua = Sessions.describeSession({ userAgent: navigator.userAgent });
          const d: SupportDiagnostics = { appVersion: APP_VERSION, platform, locale: live.locale, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone };
          if (ua.os !== undefined) d.os = ua.os;
          if (ua.browser !== undefined) d.browser = ua.browser;
          const screen = Shell.parseUrl(live.activeUrl)?.type;
          if (screen !== undefined) d.screen = screen;
          d.connection = live.realtime;
          return d;
        },
      }),
    [container, wsId, platform, live],
  );
  useEffect(() => () => vm.dispose(), [vm]);

  const askSupport = () => {
    container.api.supportDm(wsId).then(
      () => open(Shell.formatUrl({ type: "dm", ws: ws.slug, userId: Support.SUPPORT_USER_ID })),
      () => setToast({ message: t("support.help.askFailed") }),
    );
  };

  const openFeedback = () => {
    vm.reset(vm.state.kind);
    setFeedbackOpen(true);
  };

  const onSent = (r: SupportRequestResult) => {
    // Feedback reached the team: the beta banner has done its job (logic/beta afterFeedback).
    markBetaFeedbackSent();
    setToast({ message: r.duplicate === "vote" ? t("support.sentVote") : t("support.sent"), open: Shell.formatUrl({ type: "thread", ws: ws.slug, channelId: r.channelId, rootId: r.messageId }) });
  };

  const locale = prefs.locale;
  const entries: MenuEntry[] = [
      { id: "help-center", label: t("support.help.center"), icon: "lifebuoy", trailingIcon: "externalLink", onSelect: () => openLink(Support.helpCenterUrl(locale)) },
      { id: "help-whats-new", label: t("support.help.whatsNew"), icon: "megaphone", trailingIcon: "externalLink", onSelect: () => openLink(Support.whatsNewUrl(locale)) },
      { id: "help-shortcuts", label: t("support.help.shortcuts"), icon: "keyboard", hint: apple ? "⌘/" : "Ctrl+/", onSelect: onShortcuts },
      { id: "help-sep", separator: true },
      { id: "help-ask", label: t("support.help.ask"), icon: "help", hint: t("support.help.askHint"), onSelect: askSupport },
      { id: "help-feedback", label: t("support.help.feedback"), icon: "feedback", onSelect: openFeedback },
  ];
  // Then Check for updates… / Restart to update and About Bundle (its hint is the version; it replaces the caption).
  const updates = useUpdateMenuEntries();
  entries.push(...updates.entries.map((e) => (e.id === "help-about" && !("separator" in e) ? { ...e, hint: updates.aboutHint } : e)));

  const host = (
    <>
      <FeedbackSheet open={feedbackOpen} onClose={() => setFeedbackOpen(false)} vm={vm} onCapture={captureWindow} onSent={onSent} testID="feedback" />
      <Toast
        open={toast !== undefined}
        onClose={() => setToast(undefined)}
        message={toast?.message ?? ""}
        action={
          toast?.open === undefined
            ? undefined
            : {
                label: t("support.open"),
                onPress: () => {
                  const url = toast.open!;
                  setToast(undefined);
                  open(url);
                },
                testID: "support-toast-open",
              }
        }
        closeLabel={t("common.close")}
        durationMs={8000}
        testID="support-toast"
      />
    </>
  );
  return { entries, badge: updates.badge, host, openFeedback, menuOpen, setMenuOpen };
}

/** The "?" at the bottom of the activity bar. */
export function HelpButton({ help }: { help: Help }) {
  const t = useT();
  return (
    <div className="activity-item activity-help" data-active={help.menuOpen ? "open" : undefined}>
      <Menu items={help.entries} side="right" align="end" open={help.menuOpen} onOpenChange={help.setMenuOpen} testID="help-menu">
        <IconButton icon="help" size="lg" accessibilityLabel={t("support.help.button")} badge={help.badge} onPress={() => undefined} testID="help-button" />
      </Menu>
    </div>
  );
}
